import { useCallback, useMemo, useState } from 'react'
import { FormFieldDefinition, ValidationRule } from '../types/form.types'

type FormErrors<T> = Partial<Record<keyof T, string>>
type FormValues<T> = Partial<T>

type UseRecordFormReturn<T> = {
  values: FormValues<T>
  errors: FormErrors<T>
  isDirty: boolean
  handleChange: (name: keyof T, value: unknown) => void
  handleReset: (initial?: FormValues<T>) => void
  validate: () => boolean
}

const validateField = (value: unknown, rules: ValidationRule): string | null => {
  if (rules.required && (value === undefined || value === null || value === '')) {
    return 'שדה חובה'
  }
  if (typeof value === 'string') {
    if (rules.minLength !== undefined && value.length < rules.minLength) {
      return `מינימום ${rules.minLength} תווים`
    }
    if (rules.maxLength !== undefined && value.length > rules.maxLength) {
      return `מקסימום ${rules.maxLength} תווים`
    }
    if (rules.pattern && !rules.pattern.test(value)) {
      return 'פורמט לא תקין'
    }
  }
  if (typeof value === 'number') {
    if (rules.min !== undefined && value < rules.min) {
      return `ערך מינימלי: ${rules.min}`
    }
    if (rules.max !== undefined && value > rules.max) {
      return `ערך מקסימלי: ${rules.max}`
    }
  }
  if (rules.customValidator) {
    return rules.customValidator(value)
  }
  return null
}

const buildInitialValues = <T>(
  fields: FormFieldDefinition<T>[],
  seed?: FormValues<T>
): FormValues<T> =>
  fields.reduce<FormValues<T>>((acc, field) => {
    const seedValue = seed?.[field.name]
    const raw =
      seedValue !== undefined ? seedValue : (field.defaultValue ?? '')
    const transformed = field.transformIn ? field.transformIn(raw) : raw
    return { ...acc, [field.name]: transformed }
  }, {})

const useRecordForm = <T>(
  fields: FormFieldDefinition<T>[],
  initialSeed?: FormValues<T>
): UseRecordFormReturn<T> => {
  const initial = useMemo(
    () => buildInitialValues(fields, initialSeed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const [values, setValues] = useState<FormValues<T>>(initial)
  const [errors, setErrors] = useState<FormErrors<T>>({})
  const [isDirty, setIsDirty] = useState<boolean>(false)

  const handleChange = useCallback(
    (name: keyof T, value: unknown): void => {
      setValues((prev) => ({ ...prev, [name]: value }))
      setIsDirty(true)
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    },
    []
  )

  const handleReset = useCallback(
    (seed?: FormValues<T>): void => {
      const next = buildInitialValues(fields, seed)
      setValues(next)
      setErrors({})
      setIsDirty(false)
    },
    [fields]
  )

  const validate = useCallback((): boolean => {
    const nextErrors: FormErrors<T> = {}
    fields.forEach((field) => {
      if (!field.validationRules) return
      const error = validateField(values[field.name], field.validationRules)
      if (error) {
        nextErrors[field.name] = error
      }
    })
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }, [fields, values])

  return { values, errors, isDirty, handleChange, handleReset, validate }
}

export default useRecordForm
